'use client';

import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { useTranslation } from '@/i18n/useTranslation';
import { openAnalyticsConsentSettings } from '@/lib/analyticsConsent';
import {
  ArrowUpRight,
  ArchiveRestore,
  BadgeCheck,
  Briefcase,
  Compass,
  FileBadge,
  LibraryBig,
  LockKeyhole,
  Scale,
  Stamp,
} from 'lucide-react';

const primaryLinks = [
  {
    href: '/register',
    label: 'Claim your ledger',
    labelKey: 'landing_footer.individual_cta_title',
    description: 'Start preserving stories, milestones, and memories.',
    descriptionKey: 'landing_footer.individual_cta_desc',
    icon: ArchiveRestore,
    tone: '#16765a',
  },
  {
    href: '/register?role=institution',
    label: 'Launch an institution portal',
    labelKey: 'landing_footer.institution_cta_title',
    description: 'Issue, verify, and steward trusted records.',
    descriptionKey: 'landing_footer.institution_cta_desc',
    icon: LibraryBig,
    tone: '#315d88',
  },
  {
    href: '/verify',
    label: 'Verify a record',
    labelKey: 'landing_footer.verify_cta_title',
    description: 'Check a credential or public proof summary.',
    descriptionKey: 'landing_footer.verify_cta_desc',
    icon: FileBadge,
    tone: '#b28a32',
  },
];

const productLinks = [
  { href: '/product', label: 'Product Overview', tKey: 'landing_footer.nav_product' },
  { href: '/interactive', label: 'Memory Lab', tKey: 'landing_footer.nav_memory_lab' },
  {
    href: '/preservation-ledger',
    label: 'Preservation Ledger',
    tKey: 'landing_footer.nav_preservation',
  },
  { href: '/trust-ledger', label: 'Trust Ledger', tKey: 'landing_footer.nav_trust' },
  { href: '/pricing', label: 'Pricing', tKey: 'landing_footer.nav_pricing' },
  { href: '/verify', label: 'Verify a Record', tKey: 'landing_footer.nav_verify' },
  { href: '/blog', label: 'Blog', tKey: 'landing_footer.nav_blog' },
];

const companyLinks = [
  { href: '/about', label: 'About', tKey: 'landing_footer.nav_about' },
  { href: '/press', label: 'Press', tKey: 'landing_footer.nav_press' },
  { href: '/contact', label: 'Contact', tKey: 'landing_footer.nav_contact' },
  {
    href: '/contact?subject=Institution+Partnership',
    label: 'Partnerships',
    tKey: 'landing_footer.nav_partnerships',
  },
  {
    href: '/contact?subject=Investor+Relations',
    label: 'Investor Relations',
    tKey: 'landing_footer.nav_investors',
  },
];

const exploreLinks = [
  { href: '/community', label: 'Community Explorer', tKey: 'landing_footer.nav_community' },
  { href: '/interactive', label: 'Memory Lab', tKey: 'landing_footer.nav_memory_lab' },
  { href: '/public-timeline', label: 'Public Records', tKey: 'landing_footer.nav_public_records' },
  { href: '/faq', label: 'FAQ', tKey: 'landing_footer.nav_faq' },
  { href: '/blog', label: 'Blog', tKey: 'landing_footer.nav_blog' },
  { href: '/verify', label: 'Verify a Record', tKey: 'landing_footer.nav_verify' },
];

const legalLinks = [
  { href: '/privacy-policy', label: 'Privacy Policy', tKey: 'landing_footer.nav_privacy' },
  { href: '/terms-of-service', label: 'Terms', tKey: 'landing_footer.nav_terms' },
  { href: '/cookie-policy', label: 'Cookies', tKey: 'landing_footer.nav_cookies' },
  {
    href: '#cookie-settings',
    label: 'Cookie Settings',
    tKey: 'landing_footer.nav_cookie_settings',
    onClick: openAnalyticsConsentSettings,
  },
];

const trustNotes = [
  { label: 'Private by default', tKey: 'landing_footer.badge1', icon: LockKeyhole },
  { label: 'Proof, not replacement', tKey: 'landing_footer.badge2', icon: BadgeCheck },
  { label: 'Records that outlast the feed', tKey: 'landing_footer.badge3', icon: Stamp },
];

type FooterLink = {
  href: string;
  label: string;
  tKey: string;
  onClick?: () => void;
};

function FooterLinkColumn({
  title,
  icon: Icon,
  links,
}: {
  title: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  links: FooterLink[];
}) {
  const { t } = useTranslation();
  return (
    <div>
      <div className="mb-4 flex items-center gap-2 text-sm font-black text-[#f7f3ea]">
        <Icon size={16} className="text-[#b28a32]" />
        {title}
      </div>
      <div className="grid gap-y-3">
        {links.map(({ href, label, tKey, onClick }) => {
          const text = t(tKey as any) || label;
          return onClick ? (
            <button
              key={label}
              type="button"
              onClick={onClick}
              className="text-left text-sm font-semibold text-[#c8d5ce] transition-colors hover:text-white"
            >
              {text}
            </button>
          ) : (
            <Link
              key={label}
              href={href}
              className="text-sm font-semibold text-[#c8d5ce] transition-colors hover:text-white"
            >
              {text}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default function LandingFooter() {
  const { t } = useTranslation();
  return (
    <footer className="public-footer relative overflow-hidden">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-[#b28a32]/70" />
      <div className="relative mx-auto max-w-screen-2xl px-6 py-12 lg:px-10 lg:py-16 2xl:px-16">
        <div className="grid gap-8 lg:grid-cols-[1.05fr_1.45fr] lg:items-stretch">
          <div className="public-card public-card-dark flex min-h-[360px] flex-col justify-between p-6 md:p-8">
            <div>
              <div className="mb-7 flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-[14px] border border-white/15 bg-white/5 p-1">
                  <AppLogo size={34} />
                </div>
                <div>
                  <p className="font-mono text-[11px] font-black uppercase tracking-[0.24em] text-[#b28a32]">
                    ERTZYX.COM
                  </p>
                  <h2 className="font-serif text-3xl tracking-tight text-[#f7f3ea]">Ertzyx</h2>
                </div>
              </div>
              <p className="max-w-md font-serif text-3xl leading-[1.08] tracking-[-0.03em] text-[#f7f3ea] md:text-4xl">
                {t('landing_footer.tagline' as any)}
              </p>
              <p className="mt-5 max-w-md text-sm leading-7 text-[#c8d5ce]">
                {t('landing_footer.body' as any)}
              </p>
            </div>
            <div className="mt-8 grid gap-2 sm:grid-cols-3">
              {trustNotes.map(({ label, tKey, icon: Icon }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-[#dfe8e2]"
                >
                  <Icon size={14} className="text-[#77b39d]" />
                  <span>{t(tKey as any) || label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-4">
            <div className="grid gap-4 md:grid-cols-3">
              {primaryLinks.map(
                ({ href, label, labelKey, description, descriptionKey, icon: Icon, tone }) => (
                  <Link
                    key={label}
                    href={href}
                    className="public-card public-card-dark group flex min-h-[190px] flex-col justify-between p-5 transition-[border-color,transform] duration-200 ease-out hover:-translate-y-0.5 hover:border-white/25 active:scale-[0.985]"
                  >
                    <div className="flex items-center justify-between">
                      <div
                        className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/5"
                        style={{ color: tone }}
                      >
                        <Icon size={21} strokeWidth={2.3} />
                      </div>
                      <ArrowUpRight
                        size={18}
                        className="text-[#8ca097] transition-transform duration-200 ease-out group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />
                    </div>
                    <div>
                      <h3 className="text-base font-black tracking-tight text-[#f7f3ea]">
                        {t(labelKey as any) || label}
                      </h3>
                      <p className="mt-2 text-sm leading-6 text-[#aebdb5]">
                        {t(descriptionKey as any) || description}
                      </p>
                    </div>
                  </Link>
                )
              )}
            </div>
            <div className="public-card public-card-dark grid gap-6 p-5 sm:grid-cols-2 md:p-6 lg:grid-cols-4">
              <FooterLinkColumn
                title={t('nav.product' as any)}
                icon={ArchiveRestore}
                links={productLinks}
              />
              <FooterLinkColumn
                title={t('landing_footer.col_company' as any)}
                icon={Briefcase}
                links={companyLinks}
              />
              <FooterLinkColumn
                title={t('sidebar.explore' as any)}
                icon={Compass}
                links={exploreLinks}
              />
              <FooterLinkColumn
                title={t('landing_footer.col_legal' as any)}
                icon={Scale}
                links={legalLinks}
              />
            </div>
          </div>
        </div>
        <div className="mt-8 flex flex-col gap-4 border-t border-white/10 pt-6 text-sm text-[#aebdb5] md:flex-row md:items-center md:justify-between">
          <p>
            &copy; 2026 <span className="font-black text-[#b28a32]">ERTZYX.COM</span>.{' '}
            {t('landing_footer.tagline_suffix' as any)}
          </p>
          <div className="flex items-center gap-3">
            <Stamp size={14} className="text-[#b28a32]" />
            <span className="font-mono text-xs text-[#8ca097]">v1.0.0</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
